/**
 * Judy collection mobile: sticky header, filter pills, card swipe + wishlist UI.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');

  const getCollectionScroller = function () {
    return document.querySelector('[data-judy-collection-scroller]');
  };

  const getCollectionScrollTop = function () {
    const scroller = getCollectionScroller();
    if (scroller instanceof HTMLElement) return scroller.scrollTop;
    return window.scrollY;
  };

  const setCollectionScrollTop = function (top, behavior) {
    const safeTop = Math.max(0, top);
    const scroller = getCollectionScroller();
    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ top: safeTop, behavior: behavior || 'instant' });
      return;
    }
    window.scrollTo({ top: safeTop, behavior: behavior || 'instant' });
  };

  const getCollectionScrollElement = function () {
    const scroller = getCollectionScroller();
    return scroller instanceof HTMLElement ? scroller : window;
  };

  const COLLECTION_SCROLL_STORAGE_KEY = 'judy:collection-mobile-scroll-y';
  const COLLECTION_SCROLL_KEEP_KEY = 'judy:collection-mobile-keep-scroll';

  const readSavedCollectionScrollY = function () {
    try {
      const raw = sessionStorage.getItem(COLLECTION_SCROLL_STORAGE_KEY);
      if (raw == null) return null;
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const writeSavedCollectionScrollY = function (y) {
    try {
      sessionStorage.setItem(COLLECTION_SCROLL_STORAGE_KEY, String(Math.max(0, Math.round(y))));
    } catch (error) {}
  };

  const markKeepCollectionScroll = function () {
    try {
      sessionStorage.setItem(COLLECTION_SCROLL_KEEP_KEY, '1');
    } catch (error) {}
  };

  const peekKeepCollectionScroll = function () {
    try {
      return sessionStorage.getItem(COLLECTION_SCROLL_KEEP_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const clearKeepCollectionScroll = function () {
    try {
      sessionStorage.removeItem(COLLECTION_SCROLL_KEEP_KEY);
    } catch (error) {}
  };

  window.addEventListener('pagehide', function () {
    if (!MOBILE_MQ.matches) return;
    if (!document.body.classList.contains('judy-collection-mobile-template')) return;
    // Sibling nav already wrote the intended Y; avoid clobbering if scroll was reset mid-nav.
    if (peekKeepCollectionScroll()) return;
    writeSavedCollectionScrollY(getCollectionScrollTop());
  });

  document.addEventListener(
    'click',
    function (event) {
      if (!MOBILE_MQ.matches) return;
      if (!document.body.classList.contains('judy-collection-mobile-template')) return;
      if (!(event.target instanceof Element)) return;

      const productLink = event.target.closest('.judy-mobile-product-card__link');
      if (productLink instanceof HTMLAnchorElement) {
        writeSavedCollectionScrollY(getCollectionScrollTop());
        return;
      }

      const siblingLink = event.target.closest(
        '.judy-collection-mobile__category-siblings .judy-collection-mobile__category-link'
      );
      if (siblingLink instanceof HTMLAnchorElement) {
        writeSavedCollectionScrollY(getCollectionScrollTop());
        markKeepCollectionScroll();
      }
    },
    true
  );

  const initScrollHeader = function (root) {
    const shell = root.querySelector('.judy-collection-mobile__header-shell');
    const header = shell?.querySelector('[data-judy-collection-header]');
    if (!shell || !header) return;

    let ticking = false;
    const SCROLL_THRESHOLD = 8;

    const update = function () {
      ticking = false;
      const scrolled = getCollectionScrollTop() > SCROLL_THRESHOLD;
      shell.classList.toggle('is-header-solid', scrolled);
      root.classList.toggle('is-header-solid', scrolled);
      header.classList.toggle('is-solid', scrolled);
    };

    const onScroll = function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    getCollectionScrollElement().addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  };

  const initBannerCarousel = function (root) {
    const banner = root.querySelector('[data-collection-banner]');
    if (!banner) return;

    const slides = banner.querySelectorAll('[data-banner-slide]');
    if (slides.length <= 1) return;

    const autoplayRaw = parseInt(banner.dataset.bannerAutoplay, 10);
    const intervalMs = Number.isFinite(autoplayRaw) && autoplayRaw >= 1500 ? autoplayRaw : 2500;
    let activeIndex = 0;
    let timerId = null;

    const setSlide = function (index) {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === activeIndex);
      });
    };

    const stop = function () {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = null;
      }
    };

    const start = function () {
      stop();
      timerId = window.setInterval(function () {
        setSlide(activeIndex + 1);
      }, intervalMs);
    };

    slides.forEach(function (slide, i) {
      if (slide.classList.contains('is-active')) {
        activeIndex = i;
      }
    });

    banner.addEventListener('touchstart', stop, { passive: true });
    banner.addEventListener('touchend', start, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    start();
  };

  let wishClickHandlerBound = false;

  const bindWishClickHandlerOnce = function () {
    if (wishClickHandlerBound) return;
    wishClickHandlerBound = true;

    document.addEventListener(
      'click',
      function (event) {
        if (!MOBILE_MQ.matches) return;
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest('[data-mobile-card-wish]');
        if (!(button instanceof HTMLButtonElement)) return;
        if (!button.closest('[data-judy-collection-mobile-grid]')) return;

        event.preventDefault();
        event.stopPropagation();

        const pressed = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-pressed', pressed ? 'false' : 'true');
        button.classList.toggle('is-active', !pressed);
      },
      true
    );
  };

  const initWishButtons = function () {
    bindWishClickHandlerOnce();
  };

  const activateDeferredSlideImage = function (slide) {
    if (!slide) return;
    const img = slide.querySelector('.judy-mobile-product-card__image--deferred');
    if (!(img instanceof HTMLImageElement)) return;
    if (img.getAttribute('src')) return;

    const deferredSrc = img.dataset.src;
    const deferredSrcset = img.dataset.srcset;
    if (deferredSrc) {
      img.src = deferredSrc;
      img.removeAttribute('data-src');
    }
    if (deferredSrcset) {
      img.srcset = deferredSrcset;
      img.removeAttribute('data-srcset');
    }
  };

  const activateDeferredMobileCard = function (card) {
    if (!(card instanceof HTMLElement)) return;
    card.querySelectorAll('[data-mobile-card-slide]').forEach(function (slide) {
      activateDeferredSlideImage(slide);
    });
    card.classList.remove('judy-mobile-product-card--deferred');
    const gridItem = card.closest('.judy-mobile-grid-item--deferred');
    if (gridItem) {
      gridItem.classList.remove('judy-mobile-grid-item--deferred');
    }
  };

  /** @type {IntersectionObserver | null} */
  let mobileDeferredCardObserver = null;

  const initDeferredMobileCards = function (scope) {
    const root = scope instanceof HTMLElement ? scope : document;
    const pending = root.querySelectorAll('.judy-mobile-grid-item--deferred');

    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      pending.forEach(function (item) {
        const card = item.querySelector('.judy-mobile-product-card--deferred');
        if (card) activateDeferredMobileCard(card);
      });
      return;
    }

    if (!mobileDeferredCardObserver) {
      mobileDeferredCardObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            const card = entry.target.querySelector('.judy-mobile-product-card--deferred');
            if (card) activateDeferredMobileCard(card);
            mobileDeferredCardObserver?.unobserve(entry.target);
          });
        },
        { rootMargin: '80px 0px 120px 0px', threshold: 0.01 }
      );
    }

    pending.forEach(function (item) {
      mobileDeferredCardObserver?.observe(item);
    });
  };

  const resetCardGallery = function (gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    setCardSlide(gallery, 0);
    gallery.dataset.swiped = 'false';
  };

  const setMobileGridLoading = function (active, mode) {
    const loadingEl = document.querySelector('[data-judy-mobile-grid-loading]');
    if (!(loadingEl instanceof HTMLElement)) return;
    loadingEl.classList.toggle('is-active', active);
    loadingEl.classList.toggle('is-pulling', mode === 'pull');
    loadingEl.hidden = !active;
    loadingEl.setAttribute('aria-hidden', active ? 'false' : 'true');
  };

  const initSnapScroll = function (root) {
    const scrollHint = root.querySelector('[data-judy-collection-scroll-hint]');
    const headerShell = root.querySelector('.judy-collection-mobile__header-shell');
    const heroSnap = root.querySelector('[data-judy-scroll-snap="hero"]');
    const catalogSnap = root.querySelector('[data-judy-scroll-snap="catalog"]');
    const scroller = getCollectionScroller();
    if (!heroSnap || !catalogSnap || !(scroller instanceof HTMLElement)) return;
    if (scroller.dataset.judySnapScrollBound === 'true') return;
    scroller.dataset.judySnapScrollBound = 'true';

    const SNAP_THRESHOLD = 28;
    const SWIPE_MIN = 8;
    const SNAP_COOLDOWN_MS = 300;
    const SCROLL_SETTLE_MS = 180;
    const CATALOG_BARRIER_DWELL_MS = 120;
    const PRODUCT_ZONE_BUFFER = 16;
    const CATALOG_TOP_EXTRA_SPACE = 12;
    const SNAP_ANIMATION_MS = 360;

    /** @type {boolean | null} */
    let hasMoreOverride = null;
    let isSettling = false;
    let lastSnapAt = 0;
    let nativeScrollEnabled = false;
    let productScrollMode = false;
    let catalogBarrierReady = true;
    let catalogDwellTimer = null;
    let snapAnimationFrame = null;
    let lastScrollY = 0;
    let lastScrollDirection = 0;
    let wasInProductZone = false;
    let settleTimer = null;
    let catalogOverscrollDuringTouch = false;

    let touchStartY = 0;
    let touchStartX = 0;
    let touchStartScrollY = 0;
    let activeTouchId = null;
    let gestureLockActive = false;
    let lockedScrollY = 0;
    let allowNativeThisGesture = false;
    let atFooterBottomGesture = false;

    const getHeaderHeight = function () {
      return headerShell instanceof HTMLElement ? headerShell.offsetHeight : 0;
    };

    const syncScrollSnapPadding = function () {
      const headerH = getHeaderHeight();
      document.documentElement.style.setProperty('--judy-collection-header-height', headerH + 'px');
      document.documentElement.style.setProperty('--judy-catalog-top-gap', CATALOG_TOP_EXTRA_SPACE + 'px');
      scroller.style.scrollPaddingTop = headerH + 'px';
    };

    const getCatalogTopInset = function () {
      return getHeaderHeight() + CATALOG_TOP_EXTRA_SPACE;
    };

    const getSafeScrollY = function () {
      return Math.max(0, scroller.scrollTop);
    };

    const getCatalogScrollTarget = function () {
      const inset = getCatalogTopInset();
      const scrollerRect = scroller.getBoundingClientRect();
      const anchorRect = catalogSnap.getBoundingClientRect();
      return Math.max(0, Math.round(scroller.scrollTop + anchorRect.top - scrollerRect.top - inset));
    };

    const alignCategoryNavTop = function () {
      const inset = getCatalogTopInset();
      const scrollerRect = scroller.getBoundingClientRect();
      const anchorRect = catalogSnap.getBoundingClientRect();
      const delta = Math.round(anchorRect.top - scrollerRect.top - inset);
      if (Math.abs(delta) > 1) {
        scroller.scrollTop += delta;
      }
    };

    const isAtCatalogTop = function () {
      const inset = getCatalogTopInset();
      const scrollerRect = scroller.getBoundingClientRect();
      const anchorRect = catalogSnap.getBoundingClientRect();
      return Math.abs(anchorRect.top - scrollerRect.top - inset) <= SNAP_THRESHOLD;
    };

    const isInHeroPanel = function (scrollY) {
      const catalogY = getCatalogScrollTarget();
      const heroMax = catalogY > 0 ? Math.min(SNAP_THRESHOLD * 2, catalogY * 0.2) : SNAP_THRESHOLD * 2;
      return scrollY <= heroMax;
    };

    const isInTransitionZone = function (scrollY) {
      const catalogY = getCatalogScrollTarget();
      return scrollY > SNAP_THRESHOLD * 2 && scrollY < catalogY - SNAP_THRESHOLD;
    };

    const isInProductZone = function (scrollY) {
      return scrollY > getCatalogScrollTarget() + PRODUCT_ZONE_BUFFER;
    };

    const isAtFooterBottom = function () {
      if (getHasMore()) return false;

      const maxY = getMaxAllowedScrollY();
      const y = getSafeScrollY();
      if (y < maxY - 8) return false;

      const footerPanel = document.querySelector('[data-judy-scroll-panel="footer"]');
      if (!(footerPanel instanceof HTMLElement)) {
        return y >= maxY - 8;
      }

      const scrollerRect = scroller.getBoundingClientRect();
      const footerRect = footerPanel.getBoundingClientRect();
      return footerRect.bottom <= scrollerRect.bottom + 12;
    };

    const canSnapDownToHero = function () {
      return isAtCatalogTop() && catalogBarrierReady;
    };

    const isHorizontalScrollTarget = function (el) {
      if (!(el instanceof Element)) return false;
      return Boolean(
        el.closest(
          '[data-collection-banner], .judy-collection-mobile__category-siblings, .judy-collection-mobile__filter-pills, .judy-collection-mobile__filter-pill-track'
        )
      );
    };

    const isProductCardHorizontalTarget = function (el) {
      if (!(el instanceof Element)) return false;
      return Boolean(el.closest('judy-mobile-card-gallery'));
    };

    const isVerticalSwipe = function (dy, dx) {
      return Math.abs(dy) >= SWIPE_MIN && Math.abs(dy) >= Math.abs(dx);
    };

    const canAcceptSnap = function () {
      return !isSettling && Date.now() - lastSnapAt >= SNAP_COOLDOWN_MS;
    };

    const setNativeScroll = function () {
      nativeScrollEnabled = true;
      scroller.classList.remove('is-native-scroll-disabled');
    };

    const enableProductBrowsing = function () {
      productScrollMode = true;
      allowNativeThisGesture = true;
      releaseGestureLock();
      setNativeScroll(true);
      catalogBarrierReady = false;
      clearCatalogDwell();
    };

    const disableProductBrowsing = function () {
      productScrollMode = false;
      allowNativeThisGesture = false;
    };

    const cancelSnapAnimation = function () {
      if (snapAnimationFrame) {
        window.cancelAnimationFrame(snapAnimationFrame);
        snapAnimationFrame = null;
      }
      scroller.classList.remove('is-snap-animating');
    };

    const clearCatalogDwell = function () {
      if (catalogDwellTimer) {
        window.clearTimeout(catalogDwellTimer);
        catalogDwellTimer = null;
      }
    };

    const scheduleCatalogBarrierDwell = function () {
      if (catalogBarrierReady || catalogDwellTimer) return;
      catalogDwellTimer = window.setTimeout(function () {
        catalogDwellTimer = null;
        if (isAtCatalogTop()) {
          catalogBarrierReady = true;
        }
      }, CATALOG_BARRIER_DWELL_MS);
    };

    const onHitCatalogBarrierFromProducts = function () {
      disableProductBrowsing();
      catalogBarrierReady = false;
      clearCatalogDwell();
      setNativeScroll(false);
      scheduleCatalogBarrierDwell();
    };

    const isTouchActive = function () {
      return activeTouchId !== null;
    };

    const finalizeCatalogBarrierAfterTouch = function () {
      const catalogY = getCatalogScrollTarget();
      const y = getSafeScrollY();

      if (!catalogOverscrollDuringTouch && y >= catalogY - SNAP_THRESHOLD) {
        catalogOverscrollDuringTouch = false;
        return false;
      }

      catalogOverscrollDuringTouch = false;
      snapToScrollY(catalogY, function () {
        alignCategoryNavTop();
        onHitCatalogBarrierFromProducts();
      });
      return true;
    };

    const readHasMoreFromDom = function () {
      const grid = document.querySelector('[data-judy-collection-mobile-grid] .product-grid');
      if (!(grid instanceof HTMLElement)) return false;

      const lastPage = Number(grid.dataset.lastPage || '1');
      const items = grid.querySelectorAll(':scope > li[data-page]');
      let maxLoadedPage = 1;
      items.forEach(function (item) {
        maxLoadedPage = Math.max(maxLoadedPage, Number(item.dataset.page || '1'));
      });
      return maxLoadedPage < lastPage;
    };

    const getHasMore = function () {
      if (typeof hasMoreOverride === 'boolean') return hasMoreOverride;
      return readHasMoreFromDom();
    };

    const syncHasMoreDataset = function () {
      const gridWrap = document.querySelector('[data-judy-collection-mobile-grid]');
      if (!(gridWrap instanceof HTMLElement)) return;
      gridWrap.dataset.judyHasMore = getHasMore() ? 'true' : 'false';
    };

    const getScrollerMaxScrollY = function () {
      return Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    };

    const getProductListBottomScrollY = function () {
      const gridWrap = document.querySelector('[data-judy-collection-mobile-grid]');
      if (!(gridWrap instanceof HTMLElement)) return null;

      const scrollerRect = scroller.getBoundingClientRect();
      const gridRect = gridWrap.getBoundingClientRect();
      const gridBottom = scroller.scrollTop + gridRect.bottom - scrollerRect.top;
      return Math.max(0, Math.round(gridBottom - scroller.clientHeight));
    };

    const getMaxAllowedScrollY = function () {
      const docMax = getScrollerMaxScrollY();
      if (!getHasMore()) return docMax;

      const productMax = getProductListBottomScrollY();
      if (productMax === null) return docMax;
      return Math.min(docMax, productMax);
    };

    const enforceProductScrollBounds = function () {
      if (!nativeScrollEnabled || isSettling || gestureLockActive) return;

      const maxY = getMaxAllowedScrollY();
      const y = getSafeScrollY();
      if (y > maxY + 2) {
        scroller.scrollTop = maxY;
      }
    };

    const enforceFooterBottomClamp = function () {
      if (!nativeScrollEnabled || isSettling || gestureLockActive) return;
      if (!isAtFooterBottom()) return;

      const maxY = getMaxAllowedScrollY();
      const y = getSafeScrollY();
      if (y > maxY) {
        scroller.scrollTop = maxY;
      }
    };

    const clearPanelSettle = function () {
      if (settleTimer) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }
    };

    const schedulePanelSettle = function (preferCatalog) {
      if (isTouchActive()) return;
      clearPanelSettle();
      settleTimer = window.setTimeout(function () {
        settleTimer = null;
        if (isTouchActive()) return;
        settleScrollPanels(preferCatalog);
      }, SCROLL_SETTLE_MS);
    };

    const settleScrollPanels = function (preferCatalog) {
      if (isSettling || gestureLockActive) return;

      const y = getSafeScrollY();
      const catalogY = getCatalogScrollTarget();

      if (y <= 1 || isAtCatalogTop()) return;
      if (y >= catalogY + PRODUCT_ZONE_BUFFER) return;

      if (y >= catalogY - SNAP_THRESHOLD) {
        snapToScrollY(catalogY, function () {
          alignCategoryNavTop();
          onHitCatalogBarrierFromProducts();
        });
        return;
      }

      const shouldOpenCatalog =
        preferCatalog || lastScrollDirection > 0 || (lastScrollDirection === 0 && y >= catalogY * 0.5);

      snapToScrollY(shouldOpenCatalog ? catalogY : 0, function () {
        if (shouldOpenCatalog) {
          alignCategoryNavTop();
          onHitCatalogBarrierFromProducts();
        } else {
          disableProductBrowsing();
          setNativeScroll(true);
          catalogBarrierReady = true;
        }
      });
    };

    const enforceCatalogBarrier = function () {
      if (isSettling || gestureLockActive) return;

      const catalogY = getCatalogScrollTarget();
      const y = getSafeScrollY();

      if (isInProductZone(y)) {
        wasInProductZone = true;
        catalogBarrierReady = false;
        clearCatalogDwell();
        return;
      }

      if (!isTouchActive() && y > 1 && y < catalogY - 1) {
        schedulePanelSettle(lastScrollDirection >= 0);
      }
    };

    const releaseGestureLock = function () {
      gestureLockActive = false;
      activeTouchId = null;
    };

    const startGestureLock = function (scrollY) {
      gestureLockActive = true;
      lockedScrollY = scrollY;
      scroller.scrollTop = scrollY;
    };

    const lockScrollPosition = function () {
      if (!gestureLockActive) return;
      if (scroller.scrollTop !== lockedScrollY) {
        scroller.scrollTop = lockedScrollY;
      }
    };

    const animateScrollTo = function (targetY, onComplete) {
      cancelSnapAnimation();
      const startY = scroller.scrollTop;
      const delta = targetY - startY;
      if (Math.abs(delta) < 2) {
        scroller.scrollTop = targetY;
        if (typeof onComplete === 'function') onComplete();
        return;
      }

      scroller.classList.add('is-snap-animating');
      const startTime = performance.now();

      const easeOutCubic = function (t) {
        return 1 - Math.pow(1 - t, 3);
      };

      const step = function (now) {
        const progress = Math.min(1, (now - startTime) / SNAP_ANIMATION_MS);
        scroller.scrollTop = Math.round(startY + delta * easeOutCubic(progress));
        if (progress < 1) {
          snapAnimationFrame = window.requestAnimationFrame(step);
          return;
        }

        snapAnimationFrame = null;
        scroller.scrollTop = targetY;
        scroller.classList.remove('is-snap-animating');
        if (typeof onComplete === 'function') onComplete();
      };

      snapAnimationFrame = window.requestAnimationFrame(step);
    };

    const snapToScrollY = function (targetY, onComplete) {
      if (!canAcceptSnap()) return false;

      releaseGestureLock();
      allowNativeThisGesture = false;
      disableProductBrowsing();
      lastSnapAt = Date.now();
      isSettling = true;
      setNativeScroll(true);

      const safeTarget = Math.max(0, Math.round(targetY));
      let finished = false;

      const finish = function () {
        if (finished) return;
        finished = true;
        cancelSnapAnimation();
        scroller.scrollTop = safeTarget;
        if (typeof onComplete === 'function') onComplete();
        lastScrollY = getSafeScrollY();
        lastScrollDirection = 0;
        isSettling = false;
        releaseGestureLock();
        updateScrollHint();
      };

      animateScrollTo(safeTarget, finish);
      window.setTimeout(finish, SNAP_ANIMATION_MS + 80);
      return true;
    };

    const scrollToCatalog = function () {
      return snapToScrollY(getCatalogScrollTarget(), function () {
        alignCategoryNavTop();
        catalogBarrierReady = true;
        clearCatalogDwell();
        disableProductBrowsing();
        setNativeScroll(false);
      });
    };

    const scrollToHero = function () {
      return snapToScrollY(0, function () {
        catalogBarrierReady = true;
        clearCatalogDwell();
        disableProductBrowsing();
        setNativeScroll(false);
      });
    };

    const getCurrentPanel = function (scrollY) {
      const catalogTop = getCatalogScrollTarget();
      const footerPanel = document.querySelector('[data-judy-scroll-panel="footer"]');

      if (footerPanel instanceof HTMLElement && !getHasMore()) {
        const scrollerRect = scroller.getBoundingClientRect();
        const footerRect = footerPanel.getBoundingClientRect();
        const footerTop = scroller.scrollTop + footerRect.top - scrollerRect.top;
        if (scrollY >= footerTop - SNAP_THRESHOLD) return 'footer';
      }

      if (scrollY >= catalogTop - SNAP_THRESHOLD) return 'catalog';
      return 'hero';
    };

    const updateScrollHint = function () {
      if (!scrollHint) return;
      scrollHint.classList.toggle('is-hidden', getCurrentPanel(getSafeScrollY()) !== 'hero');
    };

    const syncScrollModeFromPosition = function () {
      if (isSettling || gestureLockActive) return;

      const y = getSafeScrollY();
      const catalogY = getCatalogScrollTarget();

      if (y > catalogY + PRODUCT_ZONE_BUFFER) {
        wasInProductZone = true;
        enableProductBrowsing();
        return;
      }

      if (productScrollMode && y >= catalogY - SNAP_THRESHOLD) {
        setNativeScroll(true);
        return;
      }

      if (isAtCatalogTop()) {
        wasInProductZone = false;
        disableProductBrowsing();
        setNativeScroll(false);
        return;
      }

      if (isInHeroPanel(y)) {
        wasInProductZone = false;
        disableProductBrowsing();
        setNativeScroll(true);
        if (y > 1) {
          schedulePanelSettle(lastScrollDirection > 0);
        }
        return;
      }

      if (isInTransitionZone(y)) {
        disableProductBrowsing();
        setNativeScroll(true);
        schedulePanelSettle(lastScrollDirection >= 0);
      }
    };

    const syncScrollModeOnly = function () {
      cancelSnapAnimation();
      releaseGestureLock();
      isSettling = false;
      allowNativeThisGesture = false;
      atFooterBottomGesture = false;
      activeTouchId = null;
      clearCatalogDwell();
      clearPanelSettle();

      syncScrollSnapPadding();
      syncHasMoreDataset();

      const y = getSafeScrollY();
      const catalogY = getCatalogScrollTarget();
      const savedY = readSavedCollectionScrollY();
      const shouldUseProductMode =
        y > catalogY + PRODUCT_ZONE_BUFFER ||
        (savedY != null && savedY > catalogY + PRODUCT_ZONE_BUFFER && y > catalogY - SNAP_THRESHOLD);

      if (shouldUseProductMode) {
        wasInProductZone = true;
        enableProductBrowsing();
      } else if (y >= catalogY - SNAP_THRESHOLD) {
        wasInProductZone = false;
        disableProductBrowsing();
        setNativeScroll(false);
        catalogBarrierReady = true;
      } else if (isInHeroPanel(y)) {
        wasInProductZone = false;
        disableProductBrowsing();
        setNativeScroll(false);
        catalogBarrierReady = true;
      } else {
        wasInProductZone = savedY != null && savedY > catalogY + PRODUCT_ZONE_BUFFER;
        setNativeScroll(true);
      }

      lastScrollY = getSafeScrollY();
      enforceProductScrollBounds();
      enforceFooterBottomClamp();
      updateScrollHint();
    };

    const restoreCollectionPageScroll = function (fromBfCache) {
      cancelSnapAnimation();
      releaseGestureLock();
      isSettling = false;
      clearPanelSettle();

      const savedY = readSavedCollectionScrollY();
      const currentY = getSafeScrollY();
      const keepScroll = peekKeepCollectionScroll();
      let targetY = null;

      if (fromBfCache) {
        if (savedY != null && savedY > 0) {
          targetY = savedY;
        } else if (currentY > 0) {
          targetY = currentY;
        }
      } else if (keepScroll && savedY != null && savedY > 0) {
        targetY = savedY;
      } else if (savedY != null && savedY > 0 && currentY < savedY - SNAP_THRESHOLD) {
        targetY = savedY;
      }

      if (targetY == null) {
        if (keepScroll) clearKeepCollectionScroll();
        syncScrollModeOnly();
        return;
      }

      scroller.classList.add('is-restoring-scroll');
      setNativeScroll(true);

      const applyRestore = function () {
        scroller.scrollTop = targetY;
        syncScrollModeOnly();
        scroller.classList.remove('is-restoring-scroll');
        clearKeepCollectionScroll();
      };

      applyRestore();
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(applyRestore);
      });
      window.setTimeout(applyRestore, 48);
      window.setTimeout(applyRestore, 120);
    };

    const resetScrollInteractionState = syncScrollModeOnly;

    const isUpwardBrowseIntent = function (dy, dx) {
      return dy < -SWIPE_MIN && Math.abs(dy) > Math.abs(dx);
    };

    window.judyCollectionMobile = window.judyCollectionMobile || {};
    window.judyCollectionMobile.getHasMore = getHasMore;
    window.judyCollectionMobile.setHasMore = function (value) {
      hasMoreOverride = Boolean(value);
      syncHasMoreDataset();
      enforceProductScrollBounds();
    };
    window.judyCollectionMobile.refreshScrollBounds = function () {
      syncHasMoreDataset();
      syncScrollSnapPadding();
      syncScrollModeOnly();
    };
    window.judyCollectionMobile.syncScrollModeOnly = syncScrollModeOnly;
    window.judyCollectionMobile.resetScrollInteractionState = resetScrollInteractionState;
    window.judyCollectionMobile.restoreCollectionPageScroll = restoreCollectionPageScroll;
    window.judyCollectionMobile.scrollToHeroPanel = scrollToHero;
    window.judyCollectionMobile.scrollToCatalogPanel = scrollToCatalog;

    if (scrollHint instanceof HTMLElement) {
      scrollHint.addEventListener('click', function (event) {
        if (!MOBILE_MQ.matches) return;
        event.preventDefault();
        scrollToCatalog();
      });
    }

    const onTouchStart = function (event) {
      if (!MOBILE_MQ.matches) return;
      if (!document.body.classList.contains('judy-collection-mobile-template')) return;
      if (!(event.target instanceof Element)) return;
      if (!scroller.contains(event.target)) return;

      const touch = event.touches[0];
      if (!touch) return;

      touchStartY = touch.clientY;
      touchStartX = touch.clientX;
      touchStartScrollY = getSafeScrollY();
      activeTouchId = touch.identifier;
      allowNativeThisGesture = false;
      catalogOverscrollDuringTouch = false;
      clearPanelSettle();

      if (productScrollMode && nativeScrollEnabled) {
        atFooterBottomGesture = isAtFooterBottom();
        gestureLockActive = false;
        return;
      }

      if (isSettling) return;

      const atCatalog = isAtCatalogTop();
      const inHero = isInHeroPanel(touchStartScrollY);

      if (atCatalog) {
        startGestureLock(getCatalogScrollTarget());
        setNativeScroll(false);
        return;
      }

      if (inHero) {
        startGestureLock(touchStartScrollY);
        setNativeScroll(false);
        return;
      }

      if (isHorizontalScrollTarget(event.target)) return;
      if (isProductCardHorizontalTarget(event.target)) return;

      if (nativeScrollEnabled && isInProductZone(touchStartScrollY)) {
        atFooterBottomGesture = isAtFooterBottom();
        gestureLockActive = false;
      }
    };

    const onTouchMove = function (event) {
      if (!MOBILE_MQ.matches) return;
      if (!document.body.classList.contains('judy-collection-mobile-template')) return;

      const touch = Array.from(event.touches).find(function (item) {
        return item.identifier === activeTouchId;
      });
      if (!touch) return;

      const dy = touch.clientY - touchStartY;
      const dx = touch.clientX - touchStartX;

      if (atFooterBottomGesture && dy < -SWIPE_MIN && Math.abs(dy) > Math.abs(dx)) {
        event.preventDefault();
        enforceFooterBottomClamp();
        return;
      }

      if (allowNativeThisGesture) return;
      if (nativeScrollEnabled && !gestureLockActive) return;

      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= SWIPE_MIN) {
        if (gestureLockActive) {
          releaseGestureLock();
        }
        return;
      }

      if (gestureLockActive && isAtCatalogTop() && isUpwardBrowseIntent(dy, dx)) {
        enableProductBrowsing();
        return;
      }

      if (!gestureLockActive) return;

      event.preventDefault();
      lockScrollPosition();
    };

    const onTouchEnd = function (event) {
      if (!MOBILE_MQ.matches) return;
      if (!document.body.classList.contains('judy-collection-mobile-template')) return;

      const touch = event.changedTouches[0];
      if (!touch) return;
      if (activeTouchId !== null && touch.identifier !== activeTouchId) return;

      const dy = touch.clientY - touchStartY;
      const dx = touch.clientX - touchStartX;
      const startY = touchStartScrollY;
      const wasLocked = gestureLockActive;

      if (allowNativeThisGesture) {
        releaseGestureLock();
        activeTouchId = null;
        atFooterBottomGesture = false;
        if (finalizeCatalogBarrierAfterTouch()) {
          return;
        }
        schedulePanelSettle(true);
        return;
      }

      if (wasLocked && isAtCatalogTop() && isUpwardBrowseIntent(dy, dx)) {
        enableProductBrowsing();
        activeTouchId = null;
        atFooterBottomGesture = false;
        return;
      }

      releaseGestureLock();
      activeTouchId = null;
      atFooterBottomGesture = false;

      if (productScrollMode || wasInProductZone) {
        if (finalizeCatalogBarrierAfterTouch()) {
          return;
        }
      }

      if (!wasLocked || !canAcceptSnap()) {
        if (isInTransitionZone(startY)) {
          schedulePanelSettle(wasInProductZone || lastScrollY > getCatalogScrollTarget() + PRODUCT_ZONE_BUFFER);
        }
        return;
      }
      if (!isVerticalSwipe(dy, dx)) return;

      if (dy < 0 && isInHeroPanel(startY)) {
        scrollToCatalog();
        return;
      }

      if (dy > 0 && canSnapDownToHero()) {
        scrollToHero();
      }
    };

    const onTouchCancel = function () {
      releaseGestureLock();
      activeTouchId = null;
      atFooterBottomGesture = false;
      allowNativeThisGesture = false;
      finalizeCatalogBarrierAfterTouch();
      catalogOverscrollDuringTouch = false;
    };

    const onNativeSnapTouchStart = function (event) {
      if (!MOBILE_MQ.matches) return;
      const touch = event.changedTouches[0];
      if (!touch) return;

      activeTouchId = touch.identifier;
      clearPanelSettle();

      if (isSettling) {
        cancelSnapAnimation();
        isSettling = false;
        lastSnapAt = 0;
        lastScrollY = getSafeScrollY();
        lastScrollDirection = 0;
      }
    };

    const finishNativeSnapTouch = function (event) {
      const touch = event.changedTouches[0];
      if (activeTouchId !== null && touch && touch.identifier !== activeTouchId) return;

      activeTouchId = null;
      schedulePanelSettle(lastScrollDirection >= 0);
    };

    scroller.addEventListener('touchstart', onNativeSnapTouchStart, { passive: true });
    scroller.addEventListener('touchend', finishNativeSnapTouch, { passive: true });
    scroller.addEventListener('touchcancel', finishNativeSnapTouch, { passive: true });

    let scrollTicking = false;
    const onScroll = function () {
      if (gestureLockActive) {
        lockScrollPosition();
        return;
      }

      if (isSettling) return;

      const currentY = getSafeScrollY();
      const scrollDelta = currentY - lastScrollY;
      if (Math.abs(scrollDelta) > 1) {
        lastScrollDirection = scrollDelta > 0 ? 1 : -1;
      }
      if (currentY > getCatalogScrollTarget() + PRODUCT_ZONE_BUFFER) {
        wasInProductZone = true;
      }

      if (!scrollTicking) {
        scrollTicking = true;
        window.requestAnimationFrame(function () {
          scrollTicking = false;
          enforceCatalogBarrier();
          if (productScrollMode || nativeScrollEnabled) {
            enforceProductScrollBounds();
            enforceFooterBottomClamp();
          }
          if (!isTouchActive()) {
            syncScrollModeFromPosition();
          }
          updateScrollHint();
          lastScrollY = getSafeScrollY();
        });
      }
    };

    syncScrollSnapPadding();
    syncHasMoreDataset();
    disableProductBrowsing();
    setNativeScroll(false);
    catalogBarrierReady = true;
    const pendingRestoreY = readSavedCollectionScrollY();
    const shouldRestorePending =
      peekKeepCollectionScroll() || (pendingRestoreY != null && pendingRestoreY > SNAP_THRESHOLD);
    if (!shouldRestorePending && getSafeScrollY() <= SNAP_THRESHOLD) {
      scroller.scrollTop = 0;
    }
    syncScrollModeOnly();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        syncScrollSnapPadding();
        enforceCatalogBarrier();
        enforceProductScrollBounds();
        updateScrollHint();
      },
      { passive: true }
    );
    restoreCollectionPageScroll(false);
  };

  const initStableSnapScroll = function (root) {
    const scrollHint = root.querySelector('[data-judy-collection-scroll-hint]');
    const headerShell = root.querySelector('.judy-collection-mobile__header-shell');
    const heroSnap = root.querySelector('[data-judy-scroll-snap="hero"]');
    const catalogSnap = root.querySelector('[data-judy-scroll-snap="catalog"]');
    const scroller = getCollectionScroller();
    if (!heroSnap || !catalogSnap || !(scroller instanceof HTMLElement)) return;
    if (scroller.dataset.judyStableSnapBound === 'true') return;
    scroller.dataset.judyStableSnapBound = 'true';
    scroller.classList.remove('is-native-scroll-disabled');

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const SNAP_SETTLE_DELAY = 300;
    const CATALOG_EDGE_TOLERANCE = 24;
    let settleTimer = null;
    let snapAnimationFrame = null;
    let isTouching = false;
    let isSnapping = false;
    let lastScrollY = Math.max(0, scroller.scrollTop);
    let lastScrollDirection = 0;
    let updateTicking = false;
    let hasMoreOverride = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartScrollY = 0;
    let lastTouchY = 0;
    let transitionGestureCommitted = false;
    let footerGestureStartedAtEnd = false;
    let productBrowseGesture = false;

    const syncMetrics = function () {
      const headerHeight = headerShell instanceof HTMLElement ? headerShell.offsetHeight : 0;
      document.documentElement.style.setProperty('--judy-collection-header-height', headerHeight + 'px');
      document.documentElement.style.setProperty('--judy-catalog-top-gap', '12px');
      scroller.style.scrollPaddingTop = headerHeight + 'px';
    };

    const getCatalogTarget = function () {
      const headerHeight = headerShell instanceof HTMLElement ? headerShell.offsetHeight : 0;
      const scrollerRect = scroller.getBoundingClientRect();
      const catalogRect = catalogSnap.getBoundingClientRect();
      return Math.max(0, Math.round(scroller.scrollTop + catalogRect.top - scrollerRect.top - headerHeight - 12));
    };

    const readHasMoreFromDom = function () {
      const grid = document.querySelector('[data-judy-collection-mobile-grid] .product-grid');
      if (!(grid instanceof HTMLElement)) return false;
      const lastPage = Number(grid.dataset.lastPage || '1');
      const items = grid.querySelectorAll(':scope > li[data-page]');
      let maxLoadedPage = 1;
      items.forEach(function (item) {
        const page = Number(item.getAttribute('data-page') || '1');
        if (Number.isFinite(page)) maxLoadedPage = Math.max(maxLoadedPage, page);
      });
      return maxLoadedPage < lastPage;
    };

    const getHasMore = function () {
      return hasMoreOverride == null ? readHasMoreFromDom() : hasMoreOverride;
    };

    const lockFooter = function () {
      scroller.dataset.judyFooterUnlocked = 'false';
    };

    const unlockFooter = function () {
      if (getHasMore()) return;
      scroller.dataset.judyFooterUnlocked = 'true';
    };

    const isAtProductEnd = function () {
      const maxScrollY = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      return scroller.scrollTop >= maxScrollY - 8;
    };

    const syncHasMoreDataset = function () {
      const nextHasMore = getHasMore();
      const previousHasMore = scroller.dataset.hasMore;
      scroller.dataset.hasMore = nextHasMore ? 'true' : 'false';

      if (previousHasMore !== scroller.dataset.hasMore || nextHasMore) {
        lockFooter();
      }
    };

    const updateScrollHint = function () {
      if (!(scrollHint instanceof HTMLElement)) return;
      const catalogTarget = getCatalogTarget();
      const inHero = scroller.scrollTop <= Math.max(8, catalogTarget * 0.5);
      scrollHint.classList.toggle('is-hidden', !inHero);
    };

    const clearSettleTimer = function () {
      if (settleTimer) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }
    };

    const clearSnapAnimation = function () {
      if (snapAnimationFrame) {
        window.cancelAnimationFrame(snapAnimationFrame);
        snapAnimationFrame = null;
      }
    };

    const cancelSnap = function () {
      clearSnapAnimation();
      isSnapping = false;
      scroller.classList.remove('is-snap-animating');
      lastScrollY = Math.max(0, scroller.scrollTop);
      lastScrollDirection = 0;
    };

    const finishSnap = function (targetY) {
      clearSnapAnimation();
      scroller.scrollTop = targetY;
      isSnapping = false;
      scroller.classList.remove('is-snap-animating');
      lastScrollY = Math.max(0, scroller.scrollTop);
      lastScrollDirection = 0;
      updateScrollHint();
    };

    const snapTo = function (targetY) {
      const safeTarget = Math.max(0, Math.round(targetY));
      if (Math.abs(scroller.scrollTop - safeTarget) <= 1) {
        scroller.scrollTop = safeTarget;
        lastScrollY = safeTarget;
        lastScrollDirection = 0;
        updateScrollHint();
        return true;
      }

      clearSettleTimer();
      cancelSnap();
      if (reducedMotionQuery.matches) {
        finishSnap(safeTarget);
        return true;
      }

      const startY = Math.max(0, scroller.scrollTop);
      const distance = safeTarget - startY;
      const duration = Math.min(420, Math.max(240, Math.abs(distance) * 0.55));
      const startedAt = window.performance.now();

      isSnapping = true;
      scroller.classList.add('is-snap-animating');

      const animate = function (now) {
        if (!isSnapping) return;
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        scroller.scrollTop = startY + distance * eased;
        updateScrollHint();

        if (progress < 1) {
          snapAnimationFrame = window.requestAnimationFrame(animate);
          return;
        }

        finishSnap(safeTarget);
      };

      snapAnimationFrame = window.requestAnimationFrame(animate);
      return true;
    };

    const settlePanels = function () {
      settleTimer = null;
      if (isTouching || isSnapping) return;

      const y = Math.max(0, scroller.scrollTop);
      const catalogTarget = getCatalogTarget();
      if (catalogTarget <= 0) return;

      if (y <= 1) {
        scroller.scrollTop = 0;
        lastScrollY = 0;
        lastScrollDirection = 0;
        updateScrollHint();
        return;
      }

      if (Math.abs(y - catalogTarget) <= 2) {
        scroller.scrollTop = catalogTarget;
        lastScrollY = catalogTarget;
        lastScrollDirection = 0;
        updateScrollHint();
        return;
      }

      if (y > catalogTarget + CATALOG_EDGE_TOLERANCE) return;

      let targetY = 0;
      if (y >= catalogTarget) {
        targetY = catalogTarget;
      } else if (lastScrollDirection > 0) {
        targetY = catalogTarget;
      } else if (lastScrollDirection === 0 && y >= catalogTarget * 0.5) {
        targetY = catalogTarget;
      }
      snapTo(targetY);
    };

    const scheduleSettle = function () {
      if (isTouching || isSnapping) return;
      clearSettleTimer();
      const catalogTarget = getCatalogTarget();
      if (scroller.scrollTop > catalogTarget + CATALOG_EDGE_TOLERANCE) return;
      settleTimer = window.setTimeout(settlePanels, SNAP_SETTLE_DELAY);
    };

    const scheduleVisualUpdate = function () {
      if (updateTicking) return;
      updateTicking = true;
      window.requestAnimationFrame(function () {
        updateTicking = false;
        updateScrollHint();
      });
    };

    const onScroll = function () {
      const currentY = Math.max(0, scroller.scrollTop);
      const delta = currentY - lastScrollY;
      if (!isSnapping && Math.abs(delta) > 1) {
        lastScrollDirection = delta > 0 ? 1 : -1;
      }
      lastScrollY = currentY;
      scheduleVisualUpdate();
      if (!isTouching && !isSnapping) scheduleSettle();
    };

    const onTouchStart = function (event) {
      const touch = event.touches[0];
      if (!touch) return;

      isTouching = true;
      clearSettleTimer();
      if (isSnapping) cancelSnap();
      lastScrollY = Math.max(0, scroller.scrollTop);
      lastScrollDirection = 0;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      lastTouchY = touch.clientY;
      touchStartScrollY = lastScrollY;
      transitionGestureCommitted = false;
      footerGestureStartedAtEnd = !getHasMore() && isAtProductEnd();
      productBrowseGesture = false;
    };

    const onTouchMove = function (event) {
      const touch = event.touches[0];
      if (!touch) return;

      lastTouchY = touch.clientY;
      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;
      const isVerticalGesture = Math.abs(dy) >= 12 && Math.abs(dy) > Math.abs(dx);

      if (footerGestureStartedAtEnd && dy < -28 && !getHasMore()) {
        unlockFooter();
        footerGestureStartedAtEnd = false;
      }

      if (!isVerticalGesture) return;

      if (transitionGestureCommitted) {
        if (event.cancelable) event.preventDefault();
        return;
      }

      const catalogTarget = getCatalogTarget();
      if (catalogTarget <= 0) return;

      const currentY = Math.max(0, scroller.scrollTop);
      const startsAtCatalogOrProducts =
        touchStartScrollY >= catalogTarget - CATALOG_EDGE_TOLERANCE;

      if (dy < 0 && startsAtCatalogOrProducts) {
        productBrowseGesture = true;
        return;
      }

      const touchesTransitionBoundary =
        currentY > 0 && currentY <= catalogTarget + CATALOG_EDGE_TOLERANCE;
      const startsAtBoundary = touchStartScrollY <= catalogTarget + CATALOG_EDGE_TOLERANCE;
      if (!touchesTransitionBoundary && !startsAtBoundary) return;

      if (event.cancelable) event.preventDefault();
      transitionGestureCommitted = true;

      if (dy < 0) {
        snapTo(catalogTarget);
        return;
      }

      if (touchStartScrollY > catalogTarget + CATALOG_EDGE_TOLERANCE) {
        snapTo(catalogTarget);
      } else {
        snapTo(0);
      }
    };

    const onTouchEnd = function () {
      isTouching = false;
      if (footerGestureStartedAtEnd && touchStartY - lastTouchY > 28 && !getHasMore()) {
        unlockFooter();
      }
      footerGestureStartedAtEnd = false;

      if (productBrowseGesture) {
        productBrowseGesture = false;
        clearSettleTimer();
        lastScrollDirection = 1;
        return;
      }

      if (transitionGestureCommitted) {
        transitionGestureCommitted = false;
        return;
      }

      scheduleSettle();
    };

    const onTouchCancel = function () {
      isTouching = false;
      footerGestureStartedAtEnd = false;
      transitionGestureCommitted = false;
      productBrowseGesture = false;
      scheduleSettle();
    };

    const syncScrollModeOnly = function () {
      syncMetrics();
      syncHasMoreDataset();
      lastScrollY = Math.max(0, scroller.scrollTop);
      updateScrollHint();
    };

    const resetScrollInteractionState = function () {
      clearSettleTimer();
      cancelSnap();
      isTouching = false;
      transitionGestureCommitted = false;
      footerGestureStartedAtEnd = false;
      productBrowseGesture = false;
      lastScrollY = Math.max(0, scroller.scrollTop);
      lastScrollDirection = 0;
    };

    const restoreCollectionPageScroll = function (fromBfCache) {
      const savedY = readSavedCollectionScrollY();
      const currentY = Math.max(0, scroller.scrollTop);
      const keepScroll = peekKeepCollectionScroll();
      let targetY = currentY;

      if (savedY != null && (fromBfCache || keepScroll || currentY <= 1)) {
        targetY = savedY;
      }

      clearKeepCollectionScroll();
      resetScrollInteractionState();

      if (Math.abs(currentY - targetY) <= 1) {
        syncScrollModeOnly();
        return;
      }

      scroller.classList.add('is-restoring-scroll');
      scroller.scrollTop = targetY;
      window.requestAnimationFrame(function () {
        scroller.scrollTop = targetY;
        scroller.classList.remove('is-restoring-scroll');
        lastScrollY = Math.max(0, scroller.scrollTop);
        lastScrollDirection = 0;
        updateScrollHint();
      });
    };

    window.judyCollectionMobile = window.judyCollectionMobile || {};
    window.judyCollectionMobile.getHasMore = getHasMore;
    window.judyCollectionMobile.setHasMore = function (value) {
      hasMoreOverride = Boolean(value);
      syncHasMoreDataset();
    };
    window.judyCollectionMobile.lockFooter = lockFooter;
    window.judyCollectionMobile.refreshScrollBounds = syncScrollModeOnly;
    window.judyCollectionMobile.syncScrollModeOnly = syncScrollModeOnly;
    window.judyCollectionMobile.resetScrollInteractionState = resetScrollInteractionState;
    window.judyCollectionMobile.restoreCollectionPageScroll = restoreCollectionPageScroll;
    window.judyCollectionMobile.scrollToHeroPanel = function () {
      return snapTo(0);
    };
    window.judyCollectionMobile.scrollToCatalogPanel = function () {
      return snapTo(getCatalogTarget());
    };

    if (scrollHint instanceof HTMLElement) {
      scrollHint.addEventListener('click', function (event) {
        if (!MOBILE_MQ.matches) return;
        event.preventDefault();
        snapTo(getCatalogTarget());
      });
    }

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchmove', onTouchMove, { passive: false });
    scroller.addEventListener('touchend', onTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', onTouchCancel, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        syncMetrics();
        lastScrollY = Math.max(0, scroller.scrollTop);
        updateScrollHint();
      },
      { passive: true }
    );

    syncMetrics();
    syncHasMoreDataset();
    updateScrollHint();
    restoreCollectionPageScroll(false);
  };

  document.addEventListener('judy:mobile-grid-loading', function (event) {
    if (!MOBILE_MQ.matches) return;
    const detail = event.detail || {};
    setMobileGridLoading(Boolean(detail.active), detail.mode || 'load');
  });

  const setCardSlide = function (gallery, index) {
    const slides = gallery.querySelectorAll('[data-mobile-card-slide]');
    const dots = gallery.closest('.judy-mobile-product-card__inner')?.querySelectorAll('[data-mobile-card-dot]');
    const count = Math.min(slides.length, 3);
    const safeIndex = Math.max(0, Math.min(index, count - 1));

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === safeIndex);
      if (i === safeIndex) {
        activateDeferredSlideImage(slide);
      }
    });

    if (dots) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === safeIndex);
      });
    }
  };

  const initCardGalleries = function (root) {
    root.querySelectorAll('judy-mobile-card-gallery').forEach(function (gallery) {
      if (gallery.dataset.mobileGalleryInit === 'true') return;
      const track = gallery.querySelector('[data-mobile-card-track]');
      if (!track) return;

      gallery.dataset.mobileGalleryInit = 'true';

      let startX = 0;
      let startY = 0;
      let activeIndex = 0;
      const slides = gallery.querySelectorAll('[data-mobile-card-slide]');
      const count = Math.min(slides.length, 3);
      if (count <= 1) return;

      const getActiveIndex = function () {
        let idx = 0;
        slides.forEach(function (slide, i) {
          if (slide.classList.contains('is-active')) idx = i;
        });
        return idx;
      };

      track.addEventListener(
        'touchstart',
        function (event) {
          const touch = event.changedTouches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          activeIndex = getActiveIndex();
        },
        { passive: true }
      );

      track.addEventListener(
        'touchend',
        function (event) {
          const touch = event.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          if (Math.abs(dx) < 28 || Math.abs(dx) < Math.abs(dy)) return;
          gallery.dataset.swiped = 'true';
          if (dx < 0 && activeIndex < count - 1) {
            setCardSlide(gallery, activeIndex + 1);
          } else if (dx > 0 && activeIndex > 0) {
            setCardSlide(gallery, activeIndex - 1);
          }
        },
        { passive: true }
      );

      const cardLink = gallery.closest('.judy-mobile-product-card__link');
      if (cardLink instanceof HTMLAnchorElement) {
        cardLink.addEventListener(
          'click',
          function (event) {
            if (gallery.dataset.swiped === 'true') {
              event.preventDefault();
              event.stopPropagation();
              gallery.dataset.swiped = 'false';
            }
          },
          true
        );
      }

      const card = gallery.closest('.judy-mobile-product-card');
      if (card instanceof HTMLElement) {
        card.addEventListener('focusout', function (event) {
          if (card.contains(event.relatedTarget)) return;
          resetCardGallery(gallery);
        });
      }

      gallery.addEventListener('focusout', function (event) {
        if (gallery.contains(event.relatedTarget)) return;
        resetCardGallery(gallery);
      });
    });

    if (!root.dataset.judyGalleryResetBound) {
      root.dataset.judyGalleryResetBound = 'true';
      root.addEventListener(
        'pointerdown',
        function (event) {
          if (!(event.target instanceof Element)) return;
          root.querySelectorAll('judy-mobile-card-gallery').forEach(function (gallery) {
            if (gallery.contains(event.target)) return;
            resetCardGallery(gallery);
          });
        },
        true
      );
    }
  };

  const getMainCollectionSectionId = function () {
    const resultsList = document.querySelector('results-list[section-id]');
    return resultsList?.getAttribute('section-id') || null;
  };

  /** @type {AbortController | null} */
  let mobileFilterAbortController = null;

  const createScrollPositionLock = function (scrollX, scrollY) {
    const scrollTarget = getCollectionScrollElement();
    const lockScroll = function () {
      const scroller = getCollectionScroller();
      if (scroller instanceof HTMLElement) {
        if (scroller.scrollTop !== scrollY) {
          scroller.scrollTo({ top: scrollY, behavior: 'instant' });
        }
        return;
      }
      if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
      }
    };

    scrollTarget.addEventListener('scroll', lockScroll, { passive: true });
    return function releaseScrollLock() {
      scrollTarget.removeEventListener('scroll', lockScroll);
      setCollectionScrollTop(scrollY, 'instant');
    };
  };

  const restoreScrollPosition = function (scrollX, scrollY) {
    setCollectionScrollTop(scrollY, 'instant');
    window.requestAnimationFrame(function () {
      setCollectionScrollTop(scrollY, 'instant');
      window.requestAnimationFrame(function () {
        setCollectionScrollTop(scrollY, 'instant');
      });
    });
    window.setTimeout(function () {
      setCollectionScrollTop(scrollY, 'instant');
    }, 48);
  };

  const applyMobileCollectionUpdate = async function (href, root, pushHistory) {
    if (!href) return;

    const sectionId = getMainCollectionSectionId();
    if (!sectionId) {
      window.location.assign(href);
      return;
    }

    const scrollX = window.scrollX;
    const scrollY = getCollectionScrollTop();
    const releaseScrollLock = createScrollPositionLock(scrollX, scrollY);
    const previousScrollRestoration = history.scrollRestoration;
    window.judyCollectionMobile?.lockFooter?.();

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    if (pushHistory) {
      history.pushState({ mobileCollectionFilter: href }, '', href);
    }

    document.dispatchEvent(new Event(ThemeEvents.FilterUpdate));
    sectionRenderer.clearCache();

    mobileFilterAbortController?.abort();
    mobileFilterAbortController = new AbortController();

    setMobileGridLoading(true, 'load');

    const products = document.querySelector('.main-collection-products');
    const gridWrap = document.querySelector('.judy-collection-mobile-grid-wrap');
    const previousMinHeight = gridWrap instanceof HTMLElement ? gridWrap.style.minHeight : '';
    if (gridWrap instanceof HTMLElement) {
      gridWrap.style.minHeight = `${gridWrap.offsetHeight}px`;
    }

    products?.setAttribute('aria-busy', 'true');
    products?.classList.add('is-updating');

    try {
      const url = new URL(href, window.location.origin);
      await sectionRenderer.renderSection(sectionId, { url, cache: false });
      syncFilterPills(root);
      document.dispatchEvent(new CustomEvent('judy:collection-mobile-grid-updated'));
      restoreScrollPosition(scrollX, scrollY);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        window.location.assign(href);
      }
    } finally {
      releaseScrollLock();
      if ('scrollRestoration' in history) {
        history.scrollRestoration = previousScrollRestoration;
      }
      if (gridWrap instanceof HTMLElement) {
        gridWrap.style.minHeight = previousMinHeight;
      }
      products?.removeAttribute('aria-busy');
      products?.classList.remove('is-updating');
      setMobileGridLoading(false);
      restoreScrollPosition(scrollX, scrollY);
      window.requestAnimationFrame(function () {
        window.judyCollectionMobile?.syncScrollModeOnly?.();
      });
    }
  };

  const buildFilterToggleUrl = function (pill) {
    const param = pill.dataset.filterParam;
    const value = pill.dataset.filterValue;
    if (!param || !value) return null;

    const url = new URL(window.location.href);
    url.pathname = getCollectionBasePath();
    url.hash = '';

    const isActive = url.searchParams.getAll(param).includes(value);

    for (const key of Array.from(url.searchParams.keys())) {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
      }
    }

    if (!isActive) {
      url.searchParams.append(param, value);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');
    url.searchParams.sort();

    const query = url.searchParams.toString();
    return query ? `${url.pathname}?${query}` : url.pathname;
  };

  const getNextPriceSort = function (current) {
    if (current === 'price-ascending') return 'price-descending';
    if (current === 'price-descending') return '';
    return 'price-ascending';
  };

  const getCollectionBasePath = function () {
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    return pathname.split('/f/')[0] || pathname;
  };

  const buildSortUrl = function (sortValue) {
    const url = new URL(window.location.href);
    url.pathname = getCollectionBasePath();
    url.hash = '';

    url.searchParams.delete('sort_by');
    url.searchParams.delete('page');

    if (sortValue) {
      url.searchParams.set('sort_by', sortValue);
    }

    return url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '');
  };

  const getNextSortForPill = function (pill) {
    const url = new URL(window.location.href);
    const current = url.searchParams.get('sort_by') || '';
    const sortType = pill.dataset.mobileFilterSort;

    if (sortType === 'price') {
      return getNextPriceSort(current);
    }

    if (sortType === 'best') {
      return current === 'best-selling' ? '' : 'best-selling';
    }

    if (pill.dataset.mobileFilterType === 'new') {
      return current === 'created-descending' ? '' : 'created-descending';
    }

    return current;
  };

  const buildUrlForPillClick = function (pill) {
    const sortType = pill.dataset.mobileFilterSort;
    const filterType = pill.dataset.mobileFilterType;

    if (sortType === 'price' || sortType === 'best' || filterType === 'new') {
      return buildSortUrl(getNextSortForPill(pill));
    }

    if (filterType === 'filter') {
      return buildFilterToggleUrl(pill);
    }

    return null;
  };

  const syncFilterPills = function (root) {
    const url = new URL(window.location.href);
    const sortBy = url.searchParams.get('sort_by') || '';

    root.querySelectorAll('[data-mobile-filter-pill]').forEach(function (pill) {
      const type = pill.dataset.mobileFilterType;
      const sortType = pill.dataset.mobileFilterSort;
      let active = false;

      pill.classList.remove('is-sort-asc', 'is-sort-desc');

      if (sortType === 'price') {
        if (sortBy === 'price-ascending') {
          active = true;
          pill.classList.add('is-sort-asc');
        } else if (sortBy === 'price-descending') {
          active = true;
          pill.classList.add('is-sort-desc');
        }
      }

      if (sortType === 'best') {
        active = sortBy === 'best-selling';
        if (active) {
          pill.classList.add('is-sort-desc');
        }
      }

      if (type === 'price-asc') active = sortBy === 'price-ascending';
      if (type === 'price-desc') active = sortBy === 'price-descending';
      if (type === 'best') active = sortBy === 'best-selling';
      if (type === 'new') active = sortBy === 'created-descending';
      if (type === 'filter') {
        const param = pill.dataset.filterParam;
        const value = pill.dataset.filterValue;
        if (param && value) {
          active = url.searchParams.getAll(param).includes(value);
        }
      }

      pill.classList.toggle('is-active', active);
    });
  };

  const initFilterPills = function (root) {
    if (root.dataset.mobileFilterPillsInit === 'true') return;
    root.dataset.mobileFilterPillsInit = 'true';

    root.addEventListener('click', function (event) {
      const pill = event.target.closest('[data-mobile-filter-pill]');
      if (!(pill instanceof HTMLElement) || !root.contains(pill)) return;
      if (pill.hasAttribute('disabled')) return;

      event.preventDefault();
      event.stopPropagation();

      const sortType = pill.dataset.mobileFilterSort;
      if (sortType === 'price' || sortType === 'best' || pill.dataset.mobileFilterType === 'new') {
        const nextUrl = buildUrlForPillClick(pill);
        if (nextUrl) {
          applyMobileCollectionUpdate(nextUrl, root, true);
        }
        return;
      }

      if (pill.dataset.mobileFilterType === 'filter') {
        const toggleUrl = buildUrlForPillClick(pill);
        if (toggleUrl) {
          applyMobileCollectionUpdate(toggleUrl, root, true);
        }
      }
    });

    syncFilterPills(root);

    window.addEventListener('popstate', function () {
      if (!MOBILE_MQ.matches) return;
      const mobileRoot = document.querySelector('[data-judy-collection-mobile]');
      if (!mobileRoot) return;
      const href = window.location.pathname + window.location.search;
      applyMobileCollectionUpdate(href, mobileRoot, false);
    });
  };

  const initRoot = function (root) {
    if (root.dataset.judyCollectionMobileInit !== 'true') {
      root.dataset.judyCollectionMobileInit = 'true';
      initScrollHeader(root);
      initBannerCarousel(root);
      initFilterPills(root);
    }
    initStableSnapScroll(root);
  };

  const boot = function () {
    if (!MOBILE_MQ.matches) return;
    initWishButtons();
    document.querySelectorAll('[data-judy-collection-mobile]').forEach(initRoot);
    document.querySelectorAll('[data-judy-collection-mobile-grid]').forEach(function (gridRoot) {
      initCardGalleries(gridRoot);
      initDeferredMobileCards(gridRoot);
    });
    window.judyCollectionMobile?.restoreCollectionPageScroll?.(false);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  if (typeof MOBILE_MQ.addEventListener === 'function') {
    MOBILE_MQ.addEventListener('change', boot);
  }

  document.addEventListener('judy:collection-mobile-grid-updated', function () {
    if (!MOBILE_MQ.matches) return;
    const mobileRoot = document.querySelector('[data-judy-collection-mobile]');
    if (mobileRoot) {
      syncFilterPills(mobileRoot);
    }
    window.requestAnimationFrame(function () {
      window.judyCollectionMobile?.syncScrollModeOnly?.();
    });
    document.querySelectorAll('[data-judy-collection-mobile-grid]').forEach(function (gridRoot) {
      initCardGalleries(gridRoot);
      initDeferredMobileCards(gridRoot);
    });
  });

  window.addEventListener('pageshow', function (event) {
    if (!MOBILE_MQ.matches) return;
    if (!document.body.classList.contains('judy-collection-mobile-template')) return;
    window.judyCollectionMobile?.restoreCollectionPageScroll?.(event.persisted);
  });
})();
